import { get } from 'svelte/store';
import { devices } from 'src/stores/lists/devices';
import { encryptionKeyDefault } from 'src/constants';

export function web_ready_ios() {
	console.log('called `web_ready` to native iOS');
	fetch('shelly://web_ready/', {
		method: 'get',
	});
}

export function login_mobile_ios(email, user_api_url, lang) {
	fetch(
		`shelly://login/?username=${encodeURIComponent(email)}&user_files_url=${encodeURIComponent(
			user_api_url,
		)}/shelly_files/assets/&app_language=${lang}`,
		{
			method: 'get',
		},
	);
}

export function logout_mobile_ios(email) {
	fetch(`shelly://logout/?username=${encodeURIComponent(email)}`, {
		method: 'get',
	});
}

export function logout_all_ios() {
	fetch('shelly://logout_all', { method: 'get' });
}

export function account_switch_mobile_ios(email) {
	fetch(`shelly://account_switched/?username=${encodeURIComponent(email)}`, {
		method: 'get',
	});
}

/**
 * Hands a camera's plaintext `master_key` to native secure storage, mapped by
 * account email + device id, so push notifications can decrypt preview images.
 *
 * @param email - Normalised (lowercased) account email the key belongs to.
 * @param deviceID - The camera's device id (MAC).
 * @param masterKey - The base64 `master_key`.
 */
export function camera_master_key_ios(email: string, deviceID: string, masterKey: string) {
	fetch(
		`shelly://camera_master_key/?username=${encodeURIComponent(
			email,
		)}&deviceID=${encodeURIComponent(deviceID)}&master_key=${encodeURIComponent(masterKey)}`,
		{
			method: 'get',
		},
	);
}

export function change_lang_ios(lang) {
	// Changing hyphen to underscore to keep consistency in the native files naming.
	// Language files in web app are renamed with hyphens instead of underscore,
	// due to a bug with svelte-i18n library that doesn't accept underscore in the file name.
	// ref. issue #1778
	lang = lang.replace('-', '_');
	fetch(`shelly://language_changed/?app_language=${lang}`, {
		method: 'get',
	});
}

export function credentials_put_ios(user, pass, token) {
	fetch(`shelly://credentials_put/?usr=${encodeURIComponent(user)}&pwd=${pass}&tkn=${token}`, {
		method: 'get',
	});
}

export function get_wifi_ios() {
	fetch('shelly://get_wifi/', {
		method: 'get',
	});
}

export function include_device_ios(ssid, pass, prefix) {
	fetch(
		`shelly://include_devices/?ssid=${encodeURIComponent(
			ssid,
		)}&pass=${encodeURIComponent(pass)}&ssidPrefix=${encodeURIComponent(prefix)}`,
		{
			method: 'get',
		},
	);
}

/**
 * @function include_bluetooth_device_ios
 * this function will unite both inclusion methods for ble and gen2 bluetooth devices to user accounts
 * @param deviceID from inclusionData.selected.mac or id
 * @param deviceModel from inclusionData.selected.deviceModel. This will be used only to separate different types of connect function gen 2 vs gble
 * @param command connect(initial inclusion), update(after we check if device has update), updateDenied(if user decide not to update it's device), encrypt(final inclusion step)
 * @param devicePass for now it is necessary. In near future maybe will be optional. Must be 6 digits pin code
 * @param ssid is Wi-Fis ssid(aka network name)
 * @param pass is Wi-Fis password
 */
export function include_bluetooth_device_ios(
	deviceID,
	command,
	devicePass?,
	deviceModel?,
	ssid?,
	pass?,
) {
	if (command === 'connect') {
		if (!devicePass) {
			fetch(
				`shelly://include_bluetooth_device/?deviceID=${encodeURIComponent(
					deviceID,
				)}&command=${encodeURIComponent(command)}`,
				{
					method: 'get',
				},
			);
		} else {
			fetch(
				`shelly://include_bluetooth_device/?deviceID=${encodeURIComponent(
					deviceID,
				)}&command=${encodeURIComponent(command)}&pass=${encodeURIComponent(devicePass)}`,
				{
					method: 'get',
				},
			);
		}
	} else {
		if (typeof deviceModel !== 'undefined') {
			fetch(
				`shelly://include_bluetooth_device/?deviceID=${encodeURIComponent(
					deviceID,
				)}&command=${encodeURIComponent(command)}&pass=${encodeURIComponent(devicePass)}`,
				{
					method: 'get',
				},
			);
		} else {
			fetch(
				`shelly://include_bluetooth_device/?ssid=${encodeURIComponent(
					ssid,
				)}&pass=${encodeURIComponent(pass)}&command=${encodeURIComponent(command)}`,
				{
					method: 'get',
				},
			);
		}
	}
}

/**
 * Sends a control command to a Bluetooth device through the native iOS
 * bridge. Reads the per-device encryption key defensively — when the call
 * site doesn't (yet) have a matching entry in the `devices` store (e.g. a
 * disconnect that runs after the store was cleared, or an inclusion flow
 * where the device hasn't been registered yet), we silently skip the
 * `encKey` query param instead of crashing on `undefined.key_enc`.
 */
export function control_bluetooth_device_ios(deviceID, command) {
	const encKey = get(devices)[deviceID]?.key_enc;
	fetch(
		`shelly://control_bluetooth_device/?deviceID=${encodeURIComponent(
			deviceID,
		)}&command=${encodeURIComponent(command)}${
			encKey && encKey !== encryptionKeyDefault ? '&encKey=' + encodeURIComponent(encKey) : ''
		}`,
	);
}

export function control_bluetooth_motion_blind_time_ios(deviceID, blind_time) {
	const encKey = get(devices)[deviceID]?.key_enc;
	fetch(
		`shelly://control_bluetooth_device/?deviceID=${encodeURIComponent(
			deviceID,
		)}&command=setBlindTime&blindTime=${blind_time}${
			encKey && encKey !== encryptionKeyDefault ? '&encKey=' + encodeURIComponent(encKey) : ''
		}`,
	);
}

export function control_bluetooth_dw_min_angel_threshold_ios(deviceID, minAngleThreshold) {
	const encKey = get(devices)[deviceID]?.key_enc;
	fetch(
		`shelly://control_bluetooth_device/?deviceID=${encodeURIComponent(
			deviceID,
		)}&command=setMinAngleThreshold&minAngleThreshold=${minAngleThreshold}${
			encKey && encKey !== encryptionKeyDefault ? '&encKey=' + encodeURIComponent(encKey) : ''
		}`,
	);
}

export function control_bluetooth_motion_sensitivity_ios(deviceID, sensitivity) {
	const encKey = get(devices)[deviceID]?.key_enc;
	fetch(
		`shelly://control_bluetooth_device/?deviceID=${encodeURIComponent(
			deviceID,
		)}&command=setSensitivity&sensitivity=${sensitivity}${
			encKey && encKey !== encryptionKeyDefault ? '&encKey=' + encodeURIComponent(encKey) : ''
		}`,
	);
}

/**
 * Sends a Bluetooth control command to an iOS device via the Shelly protocol.
 * @param {string} deviceID - Unique identifier of the Bluetooth device.
 * @param {string} command - Command to send to the device.
 * @param {string} param - Query parameter key to include in the request.
 * @param {string | number} value - Value associated with the query parameter.
 * * @returns {void}
 */
export function control_bluetooth_general_ios(deviceID, command, param, value) {
	const encKey = get(devices)[deviceID]?.key_enc;

	fetch(
		`shelly://control_bluetooth_device/?deviceID=${encodeURIComponent(
			deviceID,
		)}&command=${command}&${param}=${value}${
			encKey && encKey !== encryptionKeyDefault ? '&encKey=' + encodeURIComponent(encKey) : ''
		}`,
	);
}

export function control_encrypt_bluetooth_device_ios(deviceID, pass) {
	fetch(
		`shelly://control_bluetooth_device/?deviceID=${encodeURIComponent(
			deviceID,
		)}&command=${encodeURIComponent('encrypt')}&pass=${encodeURIComponent(pass)}`,
	);
}

// unused, will be removed after full confirmation fo the new ios device inclusion ux
export function search_bluetooth_devices() {
	fetch(`shelly://search_bluetooth_devices/`, {
		method: 'get',
	});
}

export function bluetooth_device_update() {
	fetch(`shelly://include_bluetooth_device/?command=update`, {
		method: 'get',
	});
}

export function update_denied() {
	fetch(`shelly://include_bluetooth_device/?command=updateDenied`, {
		method: 'get',
	});
}

export function stop_bluetooth_search_ios() {
	fetch(`shelly://stop_bluetooth_discovery/`, {
		method: 'get',
	});
}

export function enable_device_beacon() {
	fetch(`shelly://include_bluetooth_device/?command=enableBeacon`, {
		method: 'get',
	});
}

export function disconnect_from_device_ios() {
	fetch(`shelly://include_bluetooth_device/?command=disconnect`, {
		method: 'get',
	});
}

export function abort_connection_ios() {
	fetch(`shelly://include_bluetooth_device/?command=abortConnecting`, {
		method: 'get',
	});
}

export function scan_barcode_ios(ssid, pass) {
	fetch(`shelly://scan_barcode/?ssid=${encodeURIComponent(ssid)}&pass=${encodeURIComponent(pass)}`, {
		method: 'get',
	});
}

export function scan_qr_code_ios() {
	fetch('shelly://scan_qr_code/', {
		method: 'get',
	});
}

export function init_discover_devices_ios() {
	if (import.meta.env.DEV) {
		console.log('PLS DISCOVER DEVICES');
	}
	fetch('shelly://discover_devices/', {
		method: 'get',
	});
}

export async function get_files_ios(url) {
	console.error('fetch no await, no.json()', url);
	// try {
	// let res = fetch(url)

	// console.warn('res', res)
	// return { data:res, isok:true }

	try {
		const res = await fetch(url);
		return { data: res.json(), isok: true };
	} catch (error) {
		return { isok: false };
	}

	// } catch (error) {
	// 	return { isok:false }

	// }
}

export function should_go_to() {
	console.log('called `should_go_to` to native iOS');
	fetch('shelly://should_go_to', {
		method: 'get',
	});
}
// will soon be depracated
export function should_go_to_room() {
	fetch('shelly://should_go_to_room', {
		method: 'GET',
	});
}

export function should_go_to_device() {
	fetch('shelly://should_go_to_device', {
		method: 'GET',
	});
}

export function open_external_link(url) {
	fetch(`shelly://open_url/?url=${encodeURI(url)}`, {
		method: 'GET',
	});
}

export function open_loqed_linking_ios(url) {
	fetch(`shelly://open_url/?url=${url}`, {
		method: 'GET',
	});
}

export function online_devices_ios(count) {
	fetch('shelly://online_devices/?c=' + count, {
		method: 'GET',
	});
}

export function open_amazon_linking_ios(app_url: string, web_url: string) {
	fetch(`shelly://amazon_link?app_url=${btoa(app_url)}&web_url=${btoa(web_url)}`, {
		method: 'GET',
	});
}

export function backForwardNavigation(enabled: boolean) {
	fetch(`shelly://backForwardNavigation?enabled=${enabled}`, {
		method: 'GET',
	});
}

export async function hapticFeedback(event) {
	await fetch(`shelly://trigger_feedback_event?event_type=${event}`, {
		method: 'post',
	});
}

export function disableAccessPoint_ios() {
	fetch(`shelly://include_bluetooth_device/?command=disableAccessPoint`, {
		method: 'get',
	});
}
export function disableBluetooth_ios() {
	fetch(`shelly://include_bluetooth_device/?command=disableBluetooth`, {
		method: 'get',
	});
}
export async function add_car_play_devices(devices: string[]) {
	await fetch(`shelly://carplay_devices/?d=` + devices, {
		method: 'post',
	});
}

export function rpc_over_ble_ios(deviceID, command) {
	fetch(
		`shelly://include_bluetooth_device/?deviceID=${encodeURIComponent(
			deviceID,
		)}&command=${encodeURIComponent(command)}`,
		{
			method: 'get',
		},
	);
}

export function send_brpc_ios(deviceID: string, frame: string) {
	fetch(
		`shelly://control_bluetooth_device/?deviceID=${encodeURIComponent(
			deviceID,
		)}&command=brpc&frame=${encodeURIComponent(frame)}`,
		{ method: 'get' },
	);
}

export async function authenticate_over_ble_ios(deviceID, password) {
	fetch(
		`shelly://include_bluetooth_device/?deviceID=${encodeURIComponent(
			deviceID,
		)}&command=${encodeURIComponent('setDigestPassword')}&pass=${password}`,
		{
			method: 'get',
		},
	);
}

// get if alexa app is present
export function is_alexa_app_installed_ios() {
	const req = new XMLHttpRequest();
	req.open('GET', `shelly://is_alexa_installed`);
	req.send();
}

export async function load_asset_ios(url: string, md5?: string, timeout: number = 6000) {
	let requestUrl = `shelly://load_asset/?file=${encodeURIComponent(url)}`;

	if (md5) {
		requestUrl += `&md5=${encodeURIComponent(md5)}`;
	}

	const response = await new Promise<Response>((resolve, reject) => {
		const timeoutId = setTimeout(() => {
			reject(new Error('ASSET_LOADING_TIMEOUT'));
		}, timeout);

		fetch(requestUrl)
			.then((res) => {
				resolve(res);
			})
			.catch((err) => {
				reject(err);
			})
			.finally(() => {
				clearTimeout(timeoutId);
			});
	});

	if (response.status === 404) {
		throw new Error('ASSET_NOT_FOUND');
	}
	if (response.status === 501) {
		throw new Error('NATIVE_ASSET_LOADING_NOT_SUPPORTED');
	}

	if (!response.ok) {
		throw new Error(`Failed to load asset: ${response.status}`);
	}

	const data = await response.blob();

	console.log('successfully loaded asset', response);
	return data;
}

export async function change_app_icon_ios(iconName: string = '') {
	await fetch(`shelly://change_app_icon/?icon=` + iconName, {
		method: 'post',
	});
}

export async function showBiometricAuthIos(
	fallback: string,
	description: string,
	negativeButton: string,
) {
	const subtitle = null;
	const url = `shelly://biometric_auth/?title=${fallback}&subtitle=${subtitle}&description=${description}&negativeButton=${negativeButton}`;
	const res = await fetch(url);

	return res;
}
